/**
 * ブラウザのスタックトレースを、ソースマップで元のソースの位置に戻す（#456）。
 *
 * @remarks
 * 本番ビルドはソースマップを公開している（`next.config.ts` の `productionBrowserSourceMaps`。
 * 社内システムでソースが見えてよく、エラー箇所の特定を優先する判断・2026-10-04）。
 * ブラウザの `error.stack` は公開していても圧縮後の位置のままなので、エラーが起きたときだけ
 * スタックに出てきた chunk のソースマップを取りに行き、`src/components/…tsx:行:列` に戻す。
 * 解析ライブラリ（`@jridgewell/trace-mapping`）も動的 import でそのときだけ読む。
 */
import type { TraceMap } from "@jridgewell/trace-mapping";

import { normalizeSourcePath, parseStackLine } from "@/lib/errors/stack";

/** 1 回の復元で取りに行く chunk の上限（スタックが深くても通信を抑える）。 */
const MAX_CHUNKS = 10;

/** chunk の URL → ソースマップ（取れなければ null）。同じ chunk を何度も取りに行かない。 */
const mapCache = new Map<string, Promise<TraceMap | null>>();

/** {@link resolveStack} の差し替え口（テスト用）。 */
export interface ResolveStackOptions {
  /** 取得に使う fetch（既定は `globalThis.fetch`）。 */
  fetchImpl?: typeof fetch;
  /** 取りに行ってよいオリジン（既定は `window.location.origin`）。 */
  origin?: string;
}

function isOwnChunk(file: string, origin: string): boolean {
  try {
    const url = new URL(file);
    return url.origin === origin && url.pathname.startsWith("/_next/");
  } catch {
    return false;
  }
}

/** JS の末尾の `//# sourceMappingURL=` から、ソースマップの URL（無ければ `.map` を足したもの）。 */
function sourceMapUrlOf(js: string, fileUrl: string): string {
  const marker = js.lastIndexOf("sourceMappingURL=");
  if (marker >= 0) {
    const value = js.slice(marker + "sourceMappingURL=".length).split(/\s/, 1)[0];
    if (value) return new URL(value, fileUrl).href;
  }
  return `${fileUrl}.map`;
}

async function fetchSourceMapJson(mapUrl: string, fetchImpl: typeof fetch): Promise<unknown> {
  if (mapUrl.startsWith("data:")) {
    const comma = mapUrl.indexOf(",");
    const meta = mapUrl.slice(0, comma);
    const data = mapUrl.slice(comma + 1);
    const text = meta.endsWith(";base64")
      ? new TextDecoder().decode(Uint8Array.from(atob(data), (c) => c.charCodeAt(0)))
      : decodeURIComponent(data);
    return JSON.parse(text);
  }
  const res = await fetchImpl(mapUrl);
  if (!res.ok) return null;
  return res.json();
}

async function loadMap(fileUrl: string, fetchImpl: typeof fetch): Promise<TraceMap | null> {
  try {
    const js = await fetchImpl(fileUrl).then((r) => (r.ok ? r.text() : ""));
    const mapUrl = sourceMapUrlOf(js, fileUrl);
    const json = await fetchSourceMapJson(mapUrl, fetchImpl);
    if (json == null) return null;
    const { FlattenMap } = await import("@jridgewell/trace-mapping");
    // Turbopack はセクション付き（index map）で出すので、平らにしてから引く
    return FlattenMap(json as Parameters<typeof FlattenMap>[0], mapUrl);
  } catch {
    return null;
  }
}

/**
 * スタックの各フレームを元のソースの位置（`    at src/…tsx:行:列`）に戻す。
 *
 * @param stack - ブラウザのスタック（`error.stack`）。
 * @param options - 差し替え口（テスト用）。
 * @returns 戻したスタック。1 フレームも戻せなければ null（元のスタックをそのまま使う）。
 */
export async function resolveStack(
  stack: string,
  options: ResolveStackOptions = {},
): Promise<string | null> {
  const fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const origin =
    options.origin ?? (typeof window !== "undefined" ? window.location.origin : "");
  const lines = stack.split("\n");
  const frames = lines.map(parseStackLine);
  const files = [
    ...new Set(
      frames
        .filter((f): f is NonNullable<typeof f> => f != null && isOwnChunk(f.file, origin))
        .map((f) => f.file),
    ),
  ].slice(0, MAX_CHUNKS);
  if (files.length === 0) return null;

  const maps = new Map<string, TraceMap | null>();
  await Promise.all(
    files.map(async (file) => {
      let pending = mapCache.get(file);
      if (pending == null) {
        pending = loadMap(file, fetchImpl);
        mapCache.set(file, pending);
      }
      maps.set(file, await pending);
    }),
  );

  const { originalPositionFor } = await import("@jridgewell/trace-mapping");
  let changed = false;
  const out = lines.map((line, index) => {
    const frame = frames[index];
    const map = frame ? maps.get(frame.file) : null;
    if (frame == null || map == null) return line;
    // スタックの列は 1 始まり、ソースマップは 0 始まり
    const pos = originalPositionFor(map, { line: frame.line, column: frame.column - 1 });
    if (pos.source == null) return line;
    changed = true;
    return `    at ${normalizeSourcePath(pos.source)}:${pos.line}:${pos.column + 1}`;
  });
  return changed ? out.join("\n") : null;
}
